import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AIHeroSectionProps {
  onScrollToExamples: () => void;
}

const examplePrompts = [
  'Help me automate my staff scheduling and attendance',
  'Help me automate my reporting or ERP process',
  'Help me build a tool to track inventory and orders'
];

// LLM Integration for dynamic solution generation
const generateAISolution = async (userInput: string) => {
  try {
    const prompt = `You are an AI solutions architect for small and medium businesses.

Your task is to take a user's business problem and convert it into a believable 3-step AI automation solution preview for a landing page.

Important rules:
- The response must be specific to the user's request.
- The output must always be structured as exactly 3 steps.
- The 3 steps must generally follow this pattern:
  1. Capture or organize inputs from the user's current workflow
  2. Apply AI, automation, or integration logic
  3. Deliver a useful output, action, report, or business efficiency improvement
- Keep the wording practical, simple, and business-friendly.
- Do not use vague enterprise jargon.
- Do not mention implementation uncertainty.
- Do not say "maybe", "could", or "might" unless absolutely necessary.
- Assume the business problem is solvable through a custom workflow, AI assistant, dashboard, automation, internal tool, or data integration.
- Tailor the steps to the specific use case the user gives.
- Each step title must be short, clear, and action-oriented.
- Each step description must be concise, ideally 1 sentence, maximum 20 words.
- Also choose a remix icon class name for each step (e.g., "ri-smartphone-line", "ri-brain-line", "ri-dashboard-line").

Return valid JSON only in this exact format:
{
  "action": "automate|track|manage|optimize|coordinate",
  "subject": "brief description of what's being automated",
  "steps": [
    {
      "icon": "ri-icon-name",
      "title": "Step Title",
      "description": "Brief description"
    }
  ]
}

Do not wrap the response in markdown.
Do not include any text before or after the JSON.

User business problem:
"${userInput}"`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_OPENAI_API_KEY || 'sk-proj-demo-key'}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          { role: 'system', content: 'You are a helpful AI assistant that generates structured JSON responses for business automation solutions.' },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7,
        max_tokens: 500
      })
    });

    if (!response.ok) {
      throw new Error('LLM API request failed');
    }

    const data = await response.json();
    const content = data.choices[0].message.content.trim();
    
    // Parse JSON response
    const solution = JSON.parse(content);
    return solution;
  } catch (error) {
    console.error('LLM generation error:', error);
    // Fallback to local generation if API fails
    return generateLocalSolution(userInput);
  }
};

// Fallback local solution generator
const generateLocalSolution = (input: string) => {
  const lowerInput = input.toLowerCase();
  
  if (lowerInput.includes('attendance') || lowerInput.includes('scheduling') || lowerInput.includes('staff')) {
    return {
      action: 'automate',
      subject: 'attendance and scheduling',
      steps: [
        { icon: 'ri-smartphone-line', title: 'Mobile Check-In System', description: 'Staff check in/out using their phones with GPS verification and photo capture' },
        { icon: 'ri-brain-line', title: 'AI Shift Optimization', description: 'Intelligent scheduling based on availability, skills, and workload patterns' },
        { icon: 'ri-notification-3-line', title: 'Real-Time Alerts & Reports', description: 'Instant notifications for no-shows and automated attendance reports' }
      ]
    };
  }
  
  if (lowerInput.includes('inventory') || lowerInput.includes('stock') || lowerInput.includes('warehouse')) {
    return {
      action: 'track',
      subject: 'inventory',
      steps: [
        { icon: 'ri-barcode-line', title: 'Barcode Scanning', description: 'Quick stock updates using mobile barcode scanning across all locations' },
        { icon: 'ri-line-chart-line', title: 'AI Demand Forecasting', description: 'Predict stock needs based on sales velocity and seasonal patterns' },
        { icon: 'ri-alarm-warning-line', title: 'Smart Reorder Alerts', description: 'Automatic low-stock notifications with supplier-specific reorder suggestions' }
      ]
    };
  }
  
  if (lowerInput.includes('report') || lowerInput.includes('erp') || lowerInput.includes('data')) {
    return {
      action: 'automate',
      subject: 'reporting',
      steps: [
        { icon: 'ri-database-2-line', title: 'Unified Data Collection', description: 'Automatically pull data from all your systems into one central database' },
        { icon: 'ri-dashboard-3-line', title: 'AI-Powered Insights', description: 'Generate visual dashboards with trend analysis and anomaly detection' },
        { icon: 'ri-file-pdf-line', title: 'One-Click Reports', description: 'Scheduled PDF reports automatically emailed to stakeholders' }
      ]
    };
  }
  
  return {
    action: 'automate',
    subject: 'your process',
    steps: [
      { icon: 'ri-input-method-line', title: 'Data Capture', description: 'Collect information from your current workflow automatically' },
      { icon: 'ri-cpu-line', title: 'AI Processing', description: 'Intelligent automation analyzes patterns and optimizes your operations' },
      { icon: 'ri-dashboard-line', title: 'Actionable Insights', description: 'Get real-time dashboards and automated reports with recommendations' }
    ]
  };
};

export default function AIHeroSection({ onScrollToExamples }: AIHeroSectionProps) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [processedRequest, setProcessedRequest] = useState('');
  const [solution, setSolution] = useState<any>(null);

  const handleSubmit = async (e?: React.FormEvent, autoSubmit = false) => {
    if (e) e.preventDefault();
    if (!inputValue.trim()) return;

    setIsProcessing(true);
    setShowResult(false);
    setProcessedRequest(inputValue);

    // Generate AI solution using LLM
    const generatedSolution = await generateAISolution(inputValue);
    
    // Simulate processing time for better UX
    setTimeout(() => {
      setSolution(generatedSolution);
      setIsProcessing(false);
      setShowResult(true);
    }, 2500);
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
    // Auto-submit immediately
    setTimeout(() => {
      const tempInput = prompt;
      setInputValue(tempInput);
      setIsProcessing(true);
      setShowResult(false);
      setProcessedRequest(tempInput);
      
      // Generate AI solution
      generateAISolution(tempInput).then((generatedSolution) => {
        setTimeout(() => {
          setSolution(generatedSolution);
          setIsProcessing(false);
          setShowResult(true);
        }, 2500);
      });
    }, 100);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/14379812110', '_blank');
  };

  const handlePrimaryClick = () => {
    navigate('/request-quote', { state: { mode: 'ai-tool' } });
  };

  const handleTryAnother = () => {
    setInputValue('');
    setShowResult(false);
    setIsProcessing(false);
    setSolution(null);
    setProcessedRequest('');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 pt-20">
      {/* Enhanced Futuristic AI Background Animation */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated neural network grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(20, 184, 166, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.4) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            animation: 'gridFlow 25s linear infinite'
          }}></div>
        </div>

        {/* Floating data particles */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500/15 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-400/10 rounded-full blur-3xl animate-pulse-slow"></div>
        
        {/* Moving data streams */}
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="dataGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#14B8A6', stopOpacity: 0.6 }} />
              <stop offset="100%" style={{ stopColor: '#06B6D4', stopOpacity: 0.2 }} />
            </linearGradient>
          </defs>
          <path d="M 0 200 Q 300 100 600 200 T 1200 200" stroke="url(#dataGradient)" strokeWidth="2" fill="none" className="animate-dash" />
          <path d="M 0 400 Q 400 300 800 400 T 1600 400" stroke="url(#dataGradient)" strokeWidth="2" fill="none" className="animate-dash-delayed" />
          <path d="M 0 600 Q 350 500 700 600 T 1400 600" stroke="url(#dataGradient)" strokeWidth="2" fill="none" className="animate-dash-slow" />
        </svg>

        {/* Floating binary code effect */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-0 left-1/4 text-teal-400 text-xs font-mono animate-float-code">
            01001001 01000001
          </div>
          <div className="absolute top-1/4 right-1/3 text-cyan-400 text-xs font-mono animate-float-code-delayed">
            01010000 01010010
          </div>
          <div className="absolute bottom-1/3 left-1/2 text-teal-300 text-xs font-mono animate-float-code-slow">
            01000100 01000001
          </div>
        </div>

        {/* Pulsing connection nodes */}
        <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-teal-400 rounded-full animate-pulse-node"></div>
        <div className="absolute top-1/2 right-1/3 w-3 h-3 bg-cyan-400 rounded-full animate-pulse-node-delayed"></div>
        <div className="absolute bottom-1/4 left-2/3 w-3 h-3 bg-teal-500 rounded-full animate-pulse-node-slow"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Your Business Runs on<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-600">
              People and Processes
            </span>
            <br />
            We Make Both Smarter with{' '}
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400 animate-gradient-x">
                AI
              </span>
              <span className="absolute inset-0 blur-xl bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400 opacity-50 animate-pulse-glow"></span>
              <span className="absolute -inset-2 border-2 border-teal-400/30 rounded-lg animate-border-pulse"></span>
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-400 rounded-full animate-ping-fast"></span>
            </span>
          </h1>
        </div>

        {/* AI Assistant Interface - Conversational Style */}
        <div className="max-w-6xl mx-auto mb-12">
          <div 
            className="relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Neural AI Orb */}
              <div className="relative flex-shrink-0">
                <div className={`relative w-64 h-64 transition-all duration-700 ${isHovering || isProcessing ? 'scale-110' : 'scale-100'}`}>
                  {/* Outer energy rings */}
                  <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 opacity-20 blur-3xl transition-all duration-700 ${isHovering || isProcessing ? 'animate-ping-slow scale-150' : ''}`}></div>
                  <div className={`absolute -inset-6 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 opacity-30 blur-2xl transition-all duration-700 ${isHovering || isProcessing ? 'animate-pulse-fast' : ''}`}></div>
                  <div className={`absolute -inset-12 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 opacity-20 blur-3xl transition-all duration-700 ${isHovering || isProcessing ? 'animate-ping-slow' : ''}`} style={{ animationDelay: '0.5s' }}></div>
                  
                  {/* Main neural orb with flowing effect */}
                  <div className={`absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 via-teal-500 to-cyan-600 shadow-2xl shadow-teal-500/50 transition-all duration-1000 ${isProcessing ? 'animate-morph-intense' : 'animate-morph-gentle'}`}>
                    {/* Inner glow layers with wave effect */}
                    <div className="absolute inset-4 rounded-full bg-gradient-to-br from-white/30 to-transparent animate-pulse-slow"></div>
                    <div className="absolute inset-8 rounded-full bg-gradient-to-br from-cyan-300/40 to-transparent animate-pulse-fast"></div>
                    
                    {/* Neural network pattern overlay */}
                    <div className="absolute inset-0 rounded-full opacity-30" style={{
                      backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.3) 1px, transparent 1px)',
                      backgroundSize: '20px 20px',
                      animation: 'rotate 20s linear infinite'
                    }}></div>
                    
                    {/* Breathing glow effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-300/20 to-teal-400/20 animate-breathing"></div>
                    
                    {/* Center brain icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {isProcessing ? (
                        <i className="ri-loader-4-line text-6xl text-white animate-spin"></i>
                      ) : (
                        <i className="ri-brain-line text-6xl text-white animate-pulse-slow"></i>
                      )}
                    </div>
                    
                    {/* Synaptic connections */}
                    {(isHovering || isProcessing) && (
                      <>
                        <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-white rounded-full animate-pulse-fast shadow-lg shadow-white/50"></div>
                        <div className="absolute top-1/4 right-1/4 w-3 h-3 bg-white rounded-full animate-pulse-fast shadow-lg shadow-white/50" style={{ animationDelay: '0.2s' }}></div>
                        <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-white rounded-full animate-pulse-fast shadow-lg shadow-white/50" style={{ animationDelay: '0.4s' }}></div>
                        <div className="absolute bottom-1/4 right-1/3 w-3 h-3 bg-white rounded-full animate-pulse-fast shadow-lg shadow-white/50" style={{ animationDelay: '0.6s' }}></div>
                      </>
                    )}
                  </div>
                  
                  {/* Orbiting energy particles */}
                  {(isHovering || isProcessing) && (
                    <>
                      <div className="absolute top-0 left-1/2 w-4 h-4 bg-cyan-400 rounded-full animate-orbit shadow-lg shadow-cyan-400/50"></div>
                      <div className="absolute top-0 left-1/2 w-4 h-4 bg-teal-400 rounded-full animate-orbit-delayed shadow-lg shadow-teal-400/50"></div>
                      <div className="absolute top-0 left-1/2 w-3 h-3 bg-white rounded-full animate-orbit-fast shadow-lg shadow-white/50"></div>
                    </>
                  )}
                  
                  {/* Floating particles around orb */}
                  {isProcessing && (
                    <>
                      <div className="absolute top-1/4 -right-8 w-2 h-2 bg-cyan-400 rounded-full animate-float-particle"></div>
                      <div className="absolute bottom-1/3 -left-6 w-2 h-2 bg-teal-400 rounded-full animate-float-particle-delayed"></div>
                      <div className="absolute top-2/3 -right-10 w-2 h-2 bg-white rounded-full animate-float-particle-slow"></div>
                    </>
                  )}
                </div>
              </div>

              {/* Conversational Input Area */}
              <div className="flex-1 w-full">
                {!showResult && !isProcessing && (
                  <>
                    {/* Suggested Prompts */}
                    <div className="mb-8 space-y-3">
                      <p className="text-teal-400 text-sm font-medium mb-4">Try asking:</p>
                      {examplePrompts.map((prompt, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handlePromptClick(prompt)}
                          className="inline-block px-6 py-3 bg-slate-800/40 hover:bg-slate-700/60 border border-teal-500/30 hover:border-teal-400/60 rounded-full transition-all duration-300 text-slate-200 hover:text-white mr-3 mb-3 hover:scale-105 hover:shadow-lg hover:shadow-teal-500/20 cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <i className="ri-sparkling-line text-teal-400"></i>
                            <span className="text-sm">{prompt}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Chat-style Input */}
                    <form onSubmit={handleSubmit}>
                      <div className="relative">
                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          placeholder="Or describe your automation need..."
                          className="w-full px-6 py-5 pr-16 bg-slate-800/40 backdrop-blur-sm text-white rounded-2xl border-2 border-teal-500/30 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 text-lg placeholder-slate-400 transition-all"
                        />
                        <button
                          type="submit"
                          disabled={!inputValue.trim()}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-110 shadow-lg shadow-teal-500/30 cursor-pointer"
                        >
                          <i className="ri-send-plane-fill text-white text-lg"></i>
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {/* Processing State */}
                {isProcessing && (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center gap-4 px-8 py-4 bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-teal-500/30">
                      <div className="relative">
                        <div className="w-8 h-8 border-4 border-teal-500/30 border-t-teal-400 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 w-8 h-8 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin-reverse"></div>
                      </div>
                      <span className="text-teal-400 text-lg font-medium">AI is analyzing your request...</span>
                    </div>
                    
                    {/* Spreading particles animation */}
                    <div className="relative mt-8 h-32">
                      <div className="absolute left-1/2 top-0 w-2 h-2 bg-teal-400 rounded-full animate-spread-particle-1"></div>
                      <div className="absolute left-1/2 top-0 w-2 h-2 bg-cyan-400 rounded-full animate-spread-particle-2"></div>
                      <div className="absolute left-1/2 top-0 w-2 h-2 bg-teal-300 rounded-full animate-spread-particle-3"></div>
                      <div className="absolute left-1/2 top-0 w-2 h-2 bg-cyan-300 rounded-full animate-spread-particle-4"></div>
                      <div className="absolute left-1/2 top-0 w-2 h-2 bg-teal-500 rounded-full animate-spread-particle-5"></div>
                    </div>
                  </div>
                )}

                {/* AI Response */}
                {showResult && solution && (
                  <div className="animate-fadeIn">
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                          <i className="ri-check-line text-2xl text-white"></i>
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Yes, We Can Automate This!</h3>
                          <p className="text-slate-300">Here's how we would approach your request:</p>
                        </div>
                      </div>
                    </div>

                    {/* 3-Step Solution */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      {solution.steps.map((step: any, index: number) => (
                        <div key={index} className="relative">
                          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-teal-500/20 hover:border-teal-500/40 transition-all h-full">
                            <div className="w-14 h-14 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-slow">
                              <i className={`${step.icon} text-3xl text-teal-400`}></i>
                            </div>
                            <h4 className="text-white font-semibold mb-3 text-center">Step {index + 1}: {step.title}</h4>
                            <p className="text-slate-300 text-sm text-center leading-relaxed">{step.description}</p>
                          </div>
                          
                          {/* Pulsing Arrow */}
                          {index < solution.steps.length - 1 && (
                            <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center animate-pulse-fast">
                                <i className="ri-arrow-right-line text-white text-sm"></i>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <button
                        onClick={handlePrimaryClick}
                        className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-4 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all whitespace-nowrap shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 cursor-pointer"
                      >
                        Tell Us More About Your Workflow
                      </button>
                      <button
                        onClick={handleWhatsApp}
                        className="flex-1 bg-slate-800/50 backdrop-blur-sm text-white px-6 py-4 rounded-xl font-semibold hover:bg-slate-700/60 transition-all flex items-center justify-center gap-2 whitespace-nowrap border border-teal-500/20 hover:border-teal-500/40 cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-xl"></i>
                        <span>Chat on WhatsApp</span>
                      </button>
                    </div>

                    {/* Try Another Request */}
                    <div className="text-center">
                      <button
                        onClick={handleTryAnother}
                        className="text-teal-400 hover:text-teal-300 font-medium flex items-center gap-2 mx-auto transition-all cursor-pointer"
                      >
                        <i className="ri-refresh-line"></i>
                        <span>Try another automation request</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes breathing {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes float-particle {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(100px, -100px); opacity: 0; }
        }
        @keyframes float-particle-delayed {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(-100px, -80px); opacity: 0; }
        }
        @keyframes float-particle-slow {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(120px, 60px); opacity: 0; }
        }
        @keyframes spread-particle-1 {
          0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + 200px), -100px) scale(0); opacity: 0; }
        }
        @keyframes spread-particle-2 {
          0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% - 200px), -100px) scale(0); opacity: 0; }
        }
        @keyframes spread-particle-3 {
          0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + 150px), 100px) scale(0); opacity: 0; }
        }
        @keyframes spread-particle-4 {
          0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% - 150px), 100px) scale(0); opacity: 0; }
        }
        @keyframes spread-particle-5 {
          0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -150px) scale(0); opacity: 0; }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes morph-gentle {
          0%, 100% { border-radius: 50%; transform: scale(1); }
          25% { border-radius: 48% 52% 50% 50%; transform: scale(1.02); }
          50% { border-radius: 50% 50% 48% 52%; transform: scale(1); }
          75% { border-radius: 52% 48% 52% 48%; transform: scale(1.02); }
        }
        @keyframes morph-intense {
          0%, 100% { border-radius: 50%; transform: scale(1) rotate(0deg); }
          25% { border-radius: 42% 58% 45% 55%; transform: scale(1.08) rotate(2deg); }
          50% { border-radius: 55% 45% 40% 60%; transform: scale(1.05) rotate(-2deg); }
          75% { border-radius: 48% 52% 58% 42%; transform: scale(1.08) rotate(2deg); }
        }
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(120px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
        }
        @keyframes orbit-delayed {
          0% { transform: rotate(180deg) translateX(120px) rotate(-180deg); }
          100% { transform: rotate(540deg) translateX(120px) rotate(-540deg); }
        }
        @keyframes orbit-fast {
          0% { transform: rotate(90deg) translateX(120px) rotate(-90deg); }
          100% { transform: rotate(450deg) translateX(120px) rotate(-450deg); }
        }
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-30px) scale(1.08); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(25px) scale(1.06); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.15); }
        }
        @keyframes pulse-fast {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.5); opacity: 0.15; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes ping-fast {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gridFlow {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
        @keyframes border-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes dash {
          0% { stroke-dashoffset: 1000; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes float-code {
          0% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-100px); opacity: 0.6; }
          100% { transform: translateY(-200px); opacity: 0; }
        }
        @keyframes float-code-delayed {
          0% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-120px); opacity: 0.6; }
          100% { transform: translateY(-240px); opacity: 0; }
        }
        @keyframes float-code-slow {
          0% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-80px); opacity: 0.6; }
          100% { transform: translateY(-160px); opacity: 0; }
        }
        @keyframes pulse-node {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.5); opacity: 1; }
        }
        @keyframes pulse-node-delayed {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.5); opacity: 1; }
        }
        @keyframes pulse-node-slow {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.5); opacity: 1; }
        }
        .animate-breathing {
          animation: breathing 4s ease-in-out infinite;
        }
        .animate-float-particle {
          animation: float-particle 2s ease-out forwards;
        }
        .animate-float-particle-delayed {
          animation: float-particle-delayed 2.5s ease-out forwards;
        }
        .animate-float-particle-slow {
          animation: float-particle-slow 3s ease-out forwards;
        }
        .animate-spread-particle-1 {
          animation: spread-particle-1 1.5s ease-out forwards;
        }
        .animate-spread-particle-2 {
          animation: spread-particle-2 1.5s ease-out forwards;
          animation-delay: 0.1s;
        }
        .animate-spread-particle-3 {
          animation: spread-particle-3 1.5s ease-out forwards;
          animation-delay: 0.2s;
        }
        .animate-spread-particle-4 {
          animation: spread-particle-4 1.5s ease-out forwards;
          animation-delay: 0.3s;
        }
        .animate-spread-particle-5 {
          animation: spread-particle-5 1.5s ease-out forwards;
          animation-delay: 0.4s;
        }
        .animate-spin-reverse {
          animation: spin-reverse 1s linear infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-morph-gentle {
          animation: morph-gentle 8s ease-in-out infinite;
        }
        .animate-morph-intense {
          animation: morph-intense 3s ease-in-out infinite;
        }
        .animate-orbit {
          animation: orbit 4s linear infinite;
        }
        .animate-orbit-delayed {
          animation: orbit-delayed 4s linear infinite;
        }
        .animate-orbit-fast {
          animation: orbit-fast 3s linear infinite;
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 5s ease-in-out infinite;
        }
        .animate-pulse-fast {
          animation: pulse-fast 1.5s ease-in-out infinite;
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-ping-fast {
          animation: ping-fast 1s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-border-pulse {
          animation: border-pulse 2s ease-in-out infinite;
        }
        .animate-dash {
          stroke-dasharray: 1000;
          animation: dash 8s linear infinite;
        }
        .animate-dash-delayed {
          stroke-dasharray: 1000;
          animation: dash 10s linear infinite;
          animation-delay: 2s;
        }
        .animate-dash-slow {
          stroke-dasharray: 1000;
          animation: dash 12s linear infinite;
          animation-delay: 4s;
        }
        .animate-float-code {
          animation: float-code 15s linear infinite;
        }
        .animate-float-code-delayed {
          animation: float-code-delayed 18s linear infinite;
          animation-delay: 3s;
        }
        .animate-float-code-slow {
          animation: float-code-slow 20s linear infinite;
          animation-delay: 6s;
        }
        .animate-pulse-node {
          animation: pulse-node 3s ease-in-out infinite;
        }
        .animate-pulse-node-delayed {
          animation: pulse-node-delayed 3s ease-in-out infinite;
          animation-delay: 1s;
        }
        .animate-pulse-node-slow {
          animation: pulse-node-slow 3s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </section>
  );
}